import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { GetUtilityRequest } from '@/clients/ecomm/prospectClient.types';
import { PXE_BACKEND_URL } from '@/constants/baseUrls';
import type { SolarQuoteProducts } from '@/types/models/solarQuoteEcomm/solarProducts';
import getEnv, { type Environment } from '@/utils/environment';
import type { RootState } from '../redux-store';
import { axiosBaseQuery } from '../utils/axiosBaseQuery';

const env: Environment = getEnv();

type SolarProductQueryParams = {
  state: string;
  utility: string;
  lat: number;
  long: number;
};

export const pxeBackendApi = createApi({
  reducerPath: 'pxe-backend-api',
  baseQuery: axiosBaseQuery({
    baseUrl: PXE_BACKEND_URL[env],
  }),
  endpoints: (builder) => ({
    utility: builder.query<string[], GetUtilityRequest>({
      query: (body) => ({
        url: '/utility',
        method: 'POST',
        body,
      }),
    }),
    solarProducts: builder.query<SolarQuoteProducts, SolarProductQueryParams>({
      query: (queryParams) => ({
        url: '/products/solar',
        method: 'GET',
        params: queryParams,
      }),
    }),
  }),
});

const selectProductsResult = (
  state: RootState,
  params: SolarProductQueryParams,
  _additionalArgs: unknown
) => pxeBackendApi.endpoints.solarProducts.select(params)(state)?.data;

export const selectBatteryPrices = createSelector(
  [selectProductsResult],
  (result: SolarQuoteProducts | undefined) => {
    result?.batteryPrices ?? {};
  }
);

export const selectConfigByPanelCount = createSelector(
  [selectProductsResult, (_state, _params, panelCount: number) => panelCount],
  (result, panelCount: number) => {
    const configs = result?.panelConfigs || [];
    const panelConfig = configs?.filter(
      (config) => config.count === panelCount
    );
    return panelConfig;
  }
);

export const selectFinancialAnalysisByBill = createSelector(
  [selectProductsResult, (_state, _params, billAmount: number) => billAmount],
  (result, billAmount) => {
    const allFinancialData = result?.financialAnalyses || [];
    const financialData = allFinancialData.find(
      (data) => data.monthlyBill === billAmount
    );
    const config = financialData?.panelConfigIndex
      ? result?.panelConfigs[financialData?.panelConfigIndex]
      : {};

    return { ...(financialData || {}), ...config };
  }
);

export const { useUtilityQuery, useSolarProductsQuery } = pxeBackendApi;
